import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToTop } from "../utils/scrollToTop";

const Privacy = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-8 space-y-8"
        >
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Information We Collect
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                At MYOS Salon, we collect information you provide directly to
                us, such as when you:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Book appointments online or over the phone</li>
                <li>Fill out consultation forms</li>
                <li>Sign up for our loyalty program</li>
                <li>Contact us for customer service</li>
                <li>Subscribe to our newsletter</li>
              </ul>
              <p>
                This information may include your name, email address, phone
                number, appointment preferences, and any health-related
                information relevant to your services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. How We Use Your Information
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and manage your salon services</li>
                <li>Send appointment confirmations and reminders</li>
                <li>Process payments and maintain billing records</li>
                <li>Send promotional offers and updates (with your consent)</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Information Sharing
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We do not sell, trade, or otherwise transfer your personal
                information to third parties except in the following
                circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>With your explicit consent</li>
                <li>To comply with legal requirements</li>
                <li>To protect our rights and safety</li>
                <li>With service providers who assist in our operations</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Data Security
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We implement appropriate security measures to protect your
                personal information against unauthorized access, alteration,
                disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Secure data storage and transmission</li>
                <li>Regular security assessments</li>
                <li>Employee training on data protection</li>
                <li>Limited access to personal information</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Your Rights
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Contact Us
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                If you have any questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <p className="font-medium">MYOS Salon</p>
                <div>
                  <p className="font-medium text-sm text-gray-800">
                    Payyoli Branch
                  </p>
                  <p>
                    Address: Near Anand Hospital, Payyoli, Kerala 673522
                  </p>
                  <p>Phone: +91 62384 26642</p>
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-800">
                    Koyilandy Branch
                  </p>
                  <p>
                    Address: E, 10, 128/F, PANTHALAYANI Madhavi Building, Top
                    of Dhanlaxmi Bank Building No-10 - 128/B, 10 - 128/C, 10 -
                    128/D, 10 - 128, Koyilandy, Kerala 673305
                  </p>
                  <p>Phone: +91 62386 26642</p>
                </div>
                <p>Email: myosmakeoverstudio@gmail.com</p>
              </div>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
